import React from 'react'
import styled from '@emotion/styled'
import { Button, Popconfirm, Popover, Tooltip } from 'antd'
import {
  DeleteOutlined,
  FileTextOutlined,
  PaperClipOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { Person } from '../person'
import { useResponsive } from '@organice/utils/hooks/useResponsive'
import { formatFileName } from './file-name'
import { UploadFileVersion } from './types'

export const VersionHistoryPopover: React.FC<{
  versions: UploadFileVersion[]
  uploadId?: string | number
  onDeleteVersion?: (uploadId: string | number, uid: string | number) => void
  onDeleteAll?: () => void
  deleteAllDisabled?: boolean
}> = ({
  versions,
  uploadId,
  onDeleteVersion,
  onDeleteAll,
  deleteAllDisabled,
}) => {
  const { t } = useTranslation()
  const { isMobile } = useResponsive()
  const [open, setOpen] = React.useState(false)

  const content = (
    <VersionPopoverContent>
      <VersionPopoverHeader>
        <VersionPopoverTitle>
          {t(
            versions.length === 1
              ? 'service.attribute.upload.uploadVersions_one'
              : 'service.attribute.upload.uploadVersions_other',
            {
              count: versions.length,
              defaultValue:
                versions.length === 1
                  ? '%{count} upload version'
                  : '%{count} upload versions',
            }
          )}
        </VersionPopoverTitle>
        {onDeleteAll && versions.length > 1 && (
          <Popconfirm
            title={t('service.attribute.upload.deleteAllVersions', {
              defaultValue: 'Delete this file and all versions?',
            })}
            okText={t('service.attribute.upload.deleteUploadAction', {
              defaultValue: 'Delete upload',
            })}
            cancelText={t('common.cancel')}
            onConfirm={onDeleteAll}
            disabled={deleteAllDisabled}
          >
            <Button
              icon={<DeleteOutlined />}
              size="small"
              danger
              disabled={deleteAllDisabled}
            >
              {t('service.attribute.upload.deleteAllVersionsAction', {
                defaultValue: 'Delete all',
              })}
            </Button>
          </Popconfirm>
        )}
      </VersionPopoverHeader>
      {versions.map((v, index) => {
        const isLatest = index === 0
        const canDeleteVersion = Boolean(
          onDeleteVersion && uploadId !== undefined
        )
        const canDeleteUnsavedUpload = Boolean(
          onDeleteAll && uploadId === undefined && !deleteAllDisabled
        )
        const fileName = formatFileName(v.name, v.extension)
        return (
          <VersionRow key={v.uid}>
            <VersionColFilename>
              <PaperClipOutlined />
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={v.url}
                title={fileName}
              >
                {fileName}
              </a>
              {isLatest && (
                <LatestVersionLabel>
                  {t('service.attribute.upload.latestVersion', {
                    defaultValue: 'Aktuell',
                  })}
                </LatestVersionLabel>
              )}
            </VersionColFilename>
            <VersionColMeta>
              {v.createdAt ? dayjs(v.createdAt).format('DD.MM.YY HH:mm') : null}
            </VersionColMeta>
            <VersionColMeta>
              {v.uploadedBy ? <Person user={v.uploadedBy} /> : null}
            </VersionColMeta>
            <VersionColActions>
              {(canDeleteVersion || canDeleteUnsavedUpload) && (
                <Popconfirm
                  title={
                    canDeleteVersion
                      ? t('service.attribute.upload.deleteVersion', {
                          defaultValue: 'Delete this version?',
                        })
                      : t('service.attribute.upload.deleteAllVersions', {
                          defaultValue: 'Delete this file and all versions?',
                        })
                  }
                  okText={t('service.attribute.upload.deleteUploadAction', {
                    defaultValue: 'Delete upload',
                  })}
                  cancelText={t('common.cancel')}
                  onConfirm={() => {
                    if (onDeleteVersion && uploadId !== undefined) {
                      onDeleteVersion(uploadId, v.uid)
                      return
                    }
                    onDeleteAll?.()
                  }}
                >
                  <Button icon={<DeleteOutlined />} size="small" />
                </Popconfirm>
              )}
            </VersionColActions>
          </VersionRow>
        )
      })}
    </VersionPopoverContent>
  )

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomLeft"
      arrowPointAtCenter
      align={
        isMobile
          ? {
              offset: [-32, 0],
              overflow: { adjustX: true, adjustY: true },
            }
          : undefined
      }
      overlayStyle={{
        maxWidth: isMobile ? 'calc(100vw - 32px)' : undefined,
      }}
    >
      <Tooltip
        title={t('service.attribute.upload.showVersionHistory', {
          defaultValue: 'Version history',
        })}
        mouseLeaveDelay={0}
        destroyTooltipOnHide
        overlayStyle={{ pointerEvents: 'none' }}
      >
        <Button icon={<FileTextOutlined />} size="small" />
      </Tooltip>
    </Popover>
  )
}

const VersionPopoverContent = styled.div(({ theme }) => ({
  boxSizing: 'border-box',
  minWidth: 0,
  width: 'calc(100vw - 72px)',
  maxWidth: 'calc(100vw - 72px)',
  overflow: 'hidden',
  [theme.breakpoints[3].mq]: {
    width: 'auto',
    overflow: 'visible',
    minWidth: 360,
    maxWidth: 520,
  },
}))

const VersionPopoverHeader = styled.div(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) auto',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  minWidth: 0,
  fontWeight: 600,
  marginBottom: 8,
  paddingBottom: 6,
  borderBottom: '1px solid #f0f0f0',
  fontSize: theme?.fontSizeSm ?? 12,
  color: '#888',
  '.ant-btn': {
    width: 24,
    minWidth: 24,
    height: 24,
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  '.ant-btn > span:not(.anticon)': {
    display: 'none',
  },
  [theme.breakpoints[3].mq]: {
    display: 'flex',
    gridTemplateColumns: 'none',
    '.ant-btn': {
      width: 'auto',
      minWidth: 0,
      padding: '0 7px',
    },
    '.ant-btn > span:not(.anticon)': {
      display: 'inline',
    },
  },
}))

const VersionPopoverTitle = styled.span({
  flex: '1 1 auto',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

const VersionRow = styled.div(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'max-content minmax(0, 1fr) 24px',
  columnGap: 8,
  rowGap: 6,
  minWidth: 0,
  maxWidth: '100%',
  padding: '4px 0',
  borderBottom: '1px solid #fafafa',
  ':last-child': { borderBottom: 'none' },
  fontSize: theme?.fontSizeSm ?? 12,
  [theme.breakpoints[3].mq]: {
    gridTemplateColumns: 'minmax(0, 1fr) max-content max-content 24px',
    columnGap: 8,
    rowGap: 8,
  },
}))

const VersionColFilename = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  gridColumn: '1 / -1',
  gridRow: '1',
  maxWidth: '100%',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  svg: {
    flex: '0 0 auto',
  },
  a: {
    display: 'block',
    flex: '1 1 auto',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  [theme.breakpoints[3].mq]: {
    gridColumn: 'auto',
    gridRow: 'auto',
  },
}))

const LatestVersionLabel = styled.span(({ theme }) => ({
  flex: '0 1 auto',
  minWidth: 0,
  maxWidth: 64,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  padding: '1px 5px',
  borderRadius: 4,
  backgroundColor: theme?.iconActiveShadeColor ?? '#ffd3c4',
  color: theme?.primaryColor ?? '#FF5014',
  fontSize: theme?.fontSizeSm ?? 12,
  [theme.breakpoints[3].mq]: {
    flex: '0 0 auto',
    maxWidth: 'none',
    overflow: 'visible',
  },
}))

const VersionColMeta = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  minWidth: 0,
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: '#666',
  '> *': {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  ':nth-of-type(2)': {
    gridColumn: '1',
    gridRow: '2',
  },
  ':nth-of-type(3)': {
    gridColumn: '2',
    gridRow: '2',
  },
  [theme.breakpoints[3].mq]: {
    flex: '0 0 auto',
    maxWidth: 'none',
    justifyContent: 'flex-end',
    ':nth-of-type(2)': {
      gridColumn: 'auto',
      gridRow: 'auto',
    },
    ':nth-of-type(3)': {
      gridColumn: 'auto',
      gridRow: 'auto',
    },
  },
}))

const VersionColActions = styled.div(({ theme }) => ({
  gridColumn: '3',
  gridRow: '2',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  minWidth: 24,
  width: 24,
  maxWidth: 24,
  overflow: 'hidden',
  '.ant-btn': {
    flex: '0 0 auto',
    width: 24,
    minWidth: 24,
    height: 24,
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  '.ant-btn > .anticon': {
    lineHeight: 1,
  },
  [theme.breakpoints[3].mq]: {
    gridColumn: 'auto',
    gridRow: 'auto',
    flex: '0 0 24px',
  },
}))
